import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ImageIcon from '@mui/icons-material/Image';
import InfoIcon from '@mui/icons-material/Info';
import {
  Alert,
  Box,
  Button,
  Card,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PackageDetailsImageDialog from '../components/package_details/PackageDetailsImageDialog';
import PackageDetailsOverviewItem from '../components/package_details/PackageDetailsOverviewItem';
import deliveriesService from '../services/deliveries-service';
import fileManagerService from '../services/file-manager-service';

/** The four single-URL photo fields on a delivery, with display labels. */
const DELIVERY_PHOTOS = [
  { key: 'driver_photo', label: 'Driver Photo' },
  { key: 'receipt_photo', label: 'Receipt Photo' },
  { key: 'item_photo', label: 'Item Photo' },
  { key: 'barcode_photo', label: 'Barcode Photo' }
];

/**
 * A single delivery photo. The stored value is a private S3 URL, so resolve a
 * short-lived signed URL before rendering. Clicking opens the zoom lightbox.
 */
function DeliveryPhotoCard({ url, label, onOpen }) {
  const hasUrl = Boolean(url);
  const [resolved, setResolved] = useState(null);

  useEffect(() => {
    let active = true;
    if (url && url.startsWith('http')) {
      fileManagerService.getSignedUrl(url).then((u) => {
        if (active) setResolved(u);
      });
    } else if (url) {
      setResolved(url);
    } else {
      setResolved(null);
    }
    return () => {
      active = false;
    };
  }, [url]);

  const clickable = hasUrl && Boolean(resolved);

  return (
    <Grid item xs={12} sm={6} md={3}>
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: 3,
          overflow: 'hidden',
          cursor: clickable ? 'pointer' : 'default',
          transition: 'transform 0.2s',
          '&:hover': { transform: clickable ? 'scale(1.01)' : 'none' }
        }}
        onClick={() => clickable && onOpen(resolved)}
      >
        <CardHeader
          title={label}
          titleTypographyProps={{ variant: 'subtitle2' }}
        />
        <Box
          sx={{
            height: 220,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100'
          }}
        >
          {!hasUrl ? (
            <Typography variant="body2" color="text.secondary">
              No image (skipped)
            </Typography>
          ) : resolved ? (
            <img
              src={resolved}
              alt={label}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          ) : (
            <CircularProgress size={20} />
          )}
        </Box>
      </Card>
    </Grid>
  );
}

export default function DeliveryDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');

  useEffect(() => {
    let active = true;
    const fetchDelivery = async () => {
      setLoading(true);
      try {
        const resp = await deliveriesService.getById(id);
        if (!active) return;
        if (resp && resp.success && resp.delivery) {
          setDelivery(resp.delivery);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Failed to fetch delivery:', error);
        if (active) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchDelivery();
    return () => {
      active = false;
    };
  }, [id]);

  const openLightbox = (imgUrl) => {
    setLightboxImage(imgUrl);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage('');
  };

  const handleDownload = async (imageUrl) => {
    try {
      await fileManagerService.downloadAndSaveFile(imageUrl);
    } catch (error) {
      console.error('Download error:', error);
      alert('File download failed.');
    }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleString() : 'N/A');

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (notFound || !delivery) {
    return (
      <Box sx={{ p: 3, mt: 5 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/deliveries')}
          sx={{ mb: 2 }}
        >
          Back to Deliveries
        </Button>
        <Alert severity="error">Delivery not found.</Alert>
      </Box>
    );
  }

  const overviewItems = [
    { title: 'ID', value: delivery.id },
    { title: 'PO Number', value: delivery.po_number },
    { title: 'PO Date', value: delivery.po_date },
    { title: 'PO PIN', value: delivery.po_pin },
    { title: 'PO Free Text', value: delivery.po_free_text },
    { title: 'Serial Number', value: delivery.serial_number },
    { title: 'Service', value: delivery.service },
    { title: 'Remarks', value: delivery.remarks },
    { title: 'Captured By', value: delivery.user?.username },
    { title: 'Latitude', value: delivery.captured_at_latitude },
    { title: 'Longitude', value: delivery.captured_at_longitude },
    { title: 'Address', value: delivery.captured_at_address },
    { title: 'Captured At', value: fmtDate(delivery.createdAt) },
    { title: 'Updated At', value: fmtDate(delivery.updatedAt) }
  ];

  return (
    <Box sx={{ p: 3, mt: 5 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/deliveries')}
        sx={{ mb: 2 }}
      >
        Back to Deliveries
      </Button>

      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
        Delivery #{delivery.id}
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Delivery Information
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {overviewItems.map(({ title, value }) => (
            <PackageDetailsOverviewItem
              key={title}
              title={title}
              value={value}
            />
          ))}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          <ImageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Photos
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {DELIVERY_PHOTOS.map((p) => (
            <DeliveryPhotoCard
              key={p.key}
              url={delivery[p.key]}
              label={p.label}
              onOpen={openLightbox}
            />
          ))}
        </Grid>
      </Paper>

      <PackageDetailsImageDialog
        open={lightboxOpen}
        onClose={closeLightbox}
        imageUrl={lightboxImage}
        onDownload={() => handleDownload(lightboxImage)}
      />
    </Box>
  );
}
